import { useCallback } from 'react';
import { getUploadUrl, moderateUpload } from '../apis';
import type { UploadClawFileParams, UploadClawFileResponse, UploadClawFileResult } from '../types';

export const useUploadClawFile = () => {
  const getUploadFileType = useCallback((file: File): UploadClawFileResult['file_type'] => {
    if (file.type.startsWith('image/')) {
      return 'image';
    }
    if (file.type.startsWith('video/')) {
      return 'video';
    }
    if (file.type.startsWith('audio/')) {
      return 'audio';
    }
    return 'file';
  }, []);

  const uploadClawFile = async ({
    clawId,
    file,
    folderPath,
  }: UploadClawFileParams): Promise<UploadClawFileResponse> => {
    // 后端直接把 folder_path 与 file_name 拼成 '<folder_path>/<file_name>'，
    // 所以 folder_path 不能以 '/' 结尾（否则得到 'media/sess-x//foo.md'）。
    // 入参可能来自不同路径，这里统一去掉尾斜杠/空白做防御。
    const trimmedFolderPath = folderPath?.replace(/\/+$/, '').trim();
    const uploadUrlResult = await getUploadUrl({
      claw_id: clawId,
      folder_path: trimmedFolderPath || undefined,
      file_name: file.name,
      content_type: file.type || undefined,
    }).catch(() => null);

    if (!uploadUrlResult) {
      return {
        data: null,
        error: {
          fileName: file.name,
        },
      };
    }

    const { data, error } = uploadUrlResult;

    if (error || !data?.upload_url || !data.file_path) {
      return {
        data: null,
        error: {
          reason: error?.reason,
          fileName: file.name,
        },
      };
    }

    const uploadResponse = await fetch(data.upload_url, {
      method: 'PUT',
      body: file,
    }).catch(() => null);
    if (!uploadResponse?.ok) {
      return {
        data: null,
        error: {
          fileName: file.name,
        },
      };
    }

    // PUT 成功后调用内容审核接口；审核服务不可用时降级为成功，不阻断上传
    const moderateResult = await moderateUpload({
      claw_id: clawId,
      file_path: data.file_path,
    }).catch(() => null);

    if (moderateResult?.data && !moderateResult.data.passed) {
      return {
        data: null,
        error: {
          reason: 'moderate_rejected',
          fileName: file.name,
          moderateReason: moderateResult.data.reason,
        },
      };
    }

    return {
      data: {
        file_path: data.file_path,
        file_type: getUploadFileType(file),
      },
    };
  };

  return {
    uploadClawFile,
  };
};
